import { NurseTrainingStatus, type OvertimeKind } from "@/utils/models";

/** How another surface asks the Team page to open on Wage Settings. Shape of `statusDecisions/deepLink.ts`. */

export const TEAM_WAGE_SETTINGS_PARAM = "wage_settings";

/** Which Wage Settings section to scroll to. Read only alongside `wage_settings`, like every param below. */
export const TEAM_WAGE_SETTINGS_SECTION_PARAM = "section";
/** Hours rate, overtime, break rules: the rule NAME to highlight — the fallback when `rule_id` no longer exists. */
export const TEAM_WAGE_SETTINGS_RULE_PARAM = "rule";
/**
 * Hours rate, overtime, break rules: the id of the exact rule to highlight (DAT-906). Rule ids change on
 * every Wage Settings save until the next recalculation rewrites the breakdown, so a link carries the
 * name (and, for overtime, the kind) too, and the section falls back to those when the id is gone.
 */
export const TEAM_WAGE_SETTINGS_RULE_ID_PARAM = "rule_id";
/** Overtime: which kind of overtime rule to highlight — every rule of that kind. */
export const TEAM_WAGE_SETTINGS_OVERTIME_KIND_PARAM = "overtime_kind";
/** Training & orientation: which card to highlight. */
export const TEAM_WAGE_SETTINGS_TRAINING_STATUS_PARAM = "training_status";

/** Every param a Wage Settings link carries, so the Team page can strip them together once applied. */
export const TEAM_WAGE_SETTINGS_PARAMS = [
	TEAM_WAGE_SETTINGS_PARAM,
	TEAM_WAGE_SETTINGS_SECTION_PARAM,
	TEAM_WAGE_SETTINGS_RULE_PARAM,
	TEAM_WAGE_SETTINGS_RULE_ID_PARAM,
	TEAM_WAGE_SETTINGS_OVERTIME_KIND_PARAM,
	TEAM_WAGE_SETTINGS_TRAINING_STATUS_PARAM,
] as const;

const TEAM_WAGE_SETTINGS_VALUE = "true";

/** The Wage Settings sections a deep link can land on. */
export const WAGE_SETTINGS_TARGET_SECTIONS = ["training-orientation", "hours-rate", "overtime", "break-rules"] as const;
export type WageSettingsTargetSection = (typeof WAGE_SETTINGS_TARGET_SECTIONS)[number];

/** The two statuses whose pay the Training & orientation cards set (`calculate_nurse_wage`). The rest are paid their own wage. */
export const TRAINING_PAY_STATUSES = [NurseTrainingStatus.Orientation, NurseTrainingStatus.Trainee] as const;
export type TrainingPayStatus = (typeof TRAINING_PAY_STATUSES)[number];

/** The overtime kinds a breakdown label names (`_build_ot_label`'s qualifier). */
export const OVERTIME_KINDS: readonly OvertimeKind[] = ["daily", "weekly", "consecutive"];

/**
 * One rule a link names: its id when known (the breakdown's contribution lines carry it), and its name.
 * The id is tried first; the name is the fallback for an id a later save replaced.
 */
type RuleRef = {
	/** A rule NAME, as the wage breakdown labels its rows and lines. */
	rule?: string;
	/** The rule's id when the breakdown was priced (DAT-906). */
	ruleId?: string;
};

/**
 * Where a link lands. Each section names its own kind of row; an absent one means "land on the section,
 * highlight nothing".
 */
export type WageSettingsTarget =
	| ({ section: "hours-rate" } & RuleRef)
	/** `overtimeKind`: with no rule, highlight every overtime rule that can produce that kind. */
	| ({ section: "overtime"; overtimeKind?: OvertimeKind } & RuleRef)
	/** With no rule, which row (if any) to highlight is decided by the section's own rows. */
	| ({ section: "break-rules" } & RuleRef)
	/** `trainingStatus`: the card whose rate paid the shift. */
	| { section: "training-orientation"; trainingStatus?: TrainingPayStatus };

const isTargetSection = (value: string | null): value is WageSettingsTargetSection => WAGE_SETTINGS_TARGET_SECTIONS.some((section) => section === value);
const isOvertimeKind = (value: string | null): value is OvertimeKind => OVERTIME_KINDS.some((kind) => kind === value);
const isTrainingPayStatus = (value: string | null): value is TrainingPayStatus => TRAINING_PAY_STATUSES.some((status) => status === value);

const ruleRefParamsOf = ({ rule, ruleId }: RuleRef): [string, string][] => [
	...(rule === undefined ? [] : [[TEAM_WAGE_SETTINGS_RULE_PARAM, rule] as [string, string]]),
	...(ruleId === undefined ? [] : [[TEAM_WAGE_SETTINGS_RULE_ID_PARAM, ruleId] as [string, string]]),
];

/** The row params a target carries. Exhaustive over the sections, so a new one must say what it carries. */
const rowParamsOf = (target: WageSettingsTarget): [string, string][] => {
	switch (target.section) {
		case "hours-rate":
		case "break-rules":
			return ruleRefParamsOf(target);
		case "overtime":
			return [
				...(target.overtimeKind === undefined ? [] : [[TEAM_WAGE_SETTINGS_OVERTIME_KIND_PARAM, target.overtimeKind] as [string, string]]),
				...ruleRefParamsOf(target),
			];
		case "training-orientation":
			return target.trainingStatus === undefined ? [] : [[TEAM_WAGE_SETTINGS_TRAINING_STATUS_PARAM, target.trainingStatus]];
	}
};

/** The rule a link names, read back. A blank value names nothing and is dropped. */
const parseRuleRef = (params: URLSearchParams): RuleRef => {
	const rule = params.get(TEAM_WAGE_SETTINGS_RULE_PARAM)?.trim();
	const ruleId = params.get(TEAM_WAGE_SETTINGS_RULE_ID_PARAM)?.trim();
	return { ...(rule ? { rule } : {}), ...(ruleId ? { ruleId } : {}) };
};

export function buildTeamWageSettingsUrl(departmentId: string, target?: WageSettingsTarget): string {
	// URLSearchParams, not string concatenation: a rule name is free text ("Night & Weekend", "50%").
	const params = new URLSearchParams({ [TEAM_WAGE_SETTINGS_PARAM]: TEAM_WAGE_SETTINGS_VALUE });
	if (target) {
		params.set(TEAM_WAGE_SETTINGS_SECTION_PARAM, target.section);
		for (const [key, value] of rowParamsOf(target)) params.set(key, value);
	}
	return `/${departmentId}/team?${params.toString()}`;
}

export function isTeamWageSettingsRequested(params: URLSearchParams): boolean {
	return params.get(TEAM_WAGE_SETTINGS_PARAM) === TEAM_WAGE_SETTINGS_VALUE;
}

/**
 * The section/row a Wage Settings link points at, or `undefined` when it names none — including an
 * unknown section, which then just opens the tab rather than scrolling somewhere arbitrary. A row param
 * that cannot match anything (a blank rule, an unknown overtime kind or status) is dropped: the link
 * still lands on its section, and highlights nothing.
 */
export function parseTeamWageSettingsTarget(params: URLSearchParams): WageSettingsTarget | undefined {
	if (!isTeamWageSettingsRequested(params)) return undefined;
	const section = params.get(TEAM_WAGE_SETTINGS_SECTION_PARAM);
	if (!isTargetSection(section)) return undefined;
	switch (section) {
		case "hours-rate":
		case "break-rules":
			return { section, ...parseRuleRef(params) };
		case "overtime": {
			const overtimeKind = params.get(TEAM_WAGE_SETTINGS_OVERTIME_KIND_PARAM);
			return { section, ...(isOvertimeKind(overtimeKind) ? { overtimeKind } : {}), ...parseRuleRef(params) };
		}
		case "training-orientation": {
			const trainingStatus = params.get(TEAM_WAGE_SETTINGS_TRAINING_STATUS_PARAM);
			return isTrainingPayStatus(trainingStatus) ? { section, trainingStatus } : { section };
		}
	}
}
